<script>
import {
  BANDEIRAS,
  buscarTarifaBaseConcessionaria,
  extrairTarifaBase,
  estimarTarifaFallback,
  calcularEconomia,
  consumoMedio,
} from '@/utils/simulador'
import { CONCESSIONARIAS } from '@/data/concessionarias'

export default {
  props: {
    extractedJson: { type: Object, default: null },
    form:          { type: Object, required: true },
    modoManual:    { type: Boolean, default: false },
  },

  data() {
    return { CONCESSIONARIAS }
  },

  computed: {
    extractedJsonEfetivo() {
      if (!this.modoManual) return this.extractedJson
      const consumo = parseFloat(String(this.form.kwh_avg).replace(',', '.'))
      const totalDue = parseFloat(String(this.form.valor_fatura).replace(',', '.'))
      if (!consumo || !totalDue) return null
      const conc = CONCESSIONARIAS.find(c => String(c.id) === String(this.form.concessionaria_id))
      return {
        consumption: { consumed_kwh: consumo },
        total_due: totalDue,
        company_name: conc ? conc.nome : '',
      }
    },

    tarifaBase() {
      if (!this.extractedJsonEfetivo) return null
      const fromCsv = buscarTarifaBaseConcessionaria({
        companyName:     this.extractedJsonEfetivo.company_name,
        concessionariaId: this.form.concessionaria_id,
      })
      if (fromCsv != null) return fromCsv
      const fromCharges = extrairTarifaBase(this.extractedJsonEfetivo.charges)
      if (fromCharges) return fromCharges
      return estimarTarifaFallback(this.extractedJsonEfetivo.total_due, this.consumoUltima)
    },

    consumoUltima() {
      if (!this.extractedJsonEfetivo?.consumption) return null
      const v = this.extractedJsonEfetivo.consumption.consumed_kwh
      const n = parseFloat(String(v || '').replace(',', '.'))
      return Number.isFinite(n) && n > 0 ? n : null
    },

    consumoMedioVal() {
      if (this.modoManual) return null
      if (!this.extractedJsonEfetivo?.consumption) return null
      return consumoMedio(this.extractedJsonEfetivo.consumption)
    },

    fase() {
      return this.form.tipo_fase || 'monofasico'
    },

    dadosValidos() {
      return this.tarifaBase != null && this.consumoUltima != null
    },

    linhasUltima() {
      if (!this.dadosValidos) return null
      return this.calcLinhas(this.consumoUltima)
    },

    linhasMedia() {
      if (!this.dadosValidos || this.consumoMedioVal == null) return null
      return this.calcLinhas(this.consumoMedioVal)
    },

    bandeiras() {
      return Object.entries(BANDEIRAS).map(([key, info]) => ({ key, ...info }))
    },

    creditosUltima() {
      if (!this.dadosValidos) return null
      return calcularEconomia(this.consumoUltima, this.tarifaBase, 'verde', this.fase).creditos
    },

    creditosMedia() {
      if (!this.dadosValidos || this.consumoMedioVal == null) return null
      return calcularEconomia(this.consumoMedioVal, this.tarifaBase, 'verde', this.fase).creditos
    },

    dadosFatura() {
      const j = this.extractedJsonEfetivo
      if (!j) return null
      const addr = j.address || {}
      const partes = [addr.street, addr.number, addr.complement].filter(Boolean).join(', ')
      const cidade = [addr.neighborhood, addr.city, addr.state].filter(Boolean).join(' — ')
      return {
        nome:          j.customer_name || this.form.nome || '',
        concessionaria: j.company_name || '',
        referencia:    j.billing_period?.reference || '',
        endereco:      partes,
        cidade,
        cep:           addr.zip_code ? addr.zip_code.replace(/(\d{5})(\d{3})/, '$1-$2') : '',
        totalFatura:   j.total_due || '',
        vencimento:    j.due_date || '',
      }
    },
  },

  methods: {
    calcLinhas(consumo) {
      const econ = Object.keys(BANDEIRAS).map(key =>
        calcularEconomia(consumo, this.tarifaBase, key, this.fase)
      )
      return [
        { label: 'Economia mensal',    key: 'mensal',      destaque: false },
        { label: 'Economia anual',     key: 'anual',       destaque: false },
        { label: 'Economia em 5 anos', key: 'cinco_anos',  destaque: false },
        { label: 'Economia em 10 anos', key: 'dez_anos',   destaque: true  },
      ].map(row => ({
        ...row,
        valores: econ.map(e => e[row.key]),
      }))
    },

    fmt(v) {
      if (v == null) return '—'
      return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    fmtKwh(v) {
      if (v == null) return '—'
      return v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
  },
}
</script>

<template>
  <div class="simulacao">
    <!-- Formulário manual (quando não é PDF) -->
    <div v-if="modoManual" class="form-manual">
      <div class="form-row">
        <label class="field-label">Concessionária</label>
        <select v-model="form.concessionaria_id" class="select">
          <option value="">Selecione...</option>
          <option v-for="c in CONCESSIONARIAS" :key="c.id" :value="String(c.id)">{{ c.nome }}</option>
        </select>
      </div>
      <div class="form-row">
        <label class="field-label">Tipo de instalação</label>
        <select v-model="form.tipo_fase" class="select">
          <option value="">Selecione...</option>
          <option value="monofasico">Monofásico</option>
          <option value="bifasico">Bifásico</option>
          <option value="trifasico">Trifásico</option>
        </select>
      </div>
      <div class="form-row">
        <label class="field-label">Consumo médio (kWh)</label>
        <input v-model="form.kwh_avg" type="number" min="0" class="input" placeholder="Ex: 250" inputmode="numeric" />
      </div>
      <div class="form-row">
        <label class="field-label">Valor da última fatura (R$)</label>
        <input v-model="form.valor_fatura" type="number" min="0" step="0.01" class="input" placeholder="Ex: 150.00" inputmode="decimal" />
      </div>
    </div>

    <div v-if="!dadosValidos && !modoManual" class="alert alert--danger">
      Não foi possível calcular a simulação com os dados desta fatura.
      Volte e verifique se o PDF enviado é uma conta de energia legível.
    </div>
    <div v-if="!dadosValidos && modoManual && extractedJsonEfetivo === null" class="alert alert--info" style="margin-top:0">
      Preencha os campos acima para ver a simulação.
    </div>

    <template v-if="dadosValidos">
      <!-- Card da fatura -->
      <div v-if="dadosFatura" class="card-fatura">
        <div class="card-fatura__header">
          <div class="card-fatura__empresa">{{ dadosFatura.concessionaria }}</div>
          <div class="card-fatura__ref" v-if="dadosFatura.referencia">Ref. {{ dadosFatura.referencia }}</div>
        </div>
        <div v-if="dadosFatura.nome || dadosFatura.endereco || dadosFatura.cidade" class="card-fatura__body">
          <div v-if="dadosFatura.nome" class="card-fatura__nome">{{ dadosFatura.nome }}</div>
          <div v-if="dadosFatura.endereco" class="card-fatura__endereco">
            {{ dadosFatura.endereco }}
          </div>
          <div v-if="dadosFatura.cidade" class="card-fatura__cidade">
            {{ dadosFatura.cidade }}
            <span v-if="dadosFatura.cep"> · CEP {{ dadosFatura.cep }}</span>
          </div>
        </div>
        <div class="card-fatura__footer">
          <div class="card-fatura__item" v-if="dadosFatura.totalFatura">
            <span class="card-fatura__item-label">Total da fatura</span>
            <span class="card-fatura__item-valor">R$ {{ dadosFatura.totalFatura }}</span>
          </div>
          <div class="card-fatura__item" v-if="dadosFatura.vencimento">
            <span class="card-fatura__item-label">Vencimento</span>
            <span class="card-fatura__item-valor">{{ dadosFatura.vencimento }}</span>
          </div>
          <div class="card-fatura__item">
            <span class="card-fatura__item-label">Consumo</span>
            <span class="card-fatura__item-valor">{{ fmtKwh(consumoUltima) }} KWH</span>
          </div>
        </div>
      </div>

      <!-- Bloco 1: última fatura -->
      <div class="bloco">
        <div class="bloco__head">
          <span class="bloco__titulo">1 — Estimativa de economia (com base na fatura analisada)</span>
          <span class="bloco__badge">Bandeira Vigente</span>
        </div>
        <table class="tabela">
          <thead>
            <tr>
              <th class="th-consumo"></th>
              <th
                v-for="b in bandeiras"
                :key="b.key"
                class="th-bandeira"
                :class="'th--' + b.key"
              >{{ b.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="linha in linhasUltima"
              :key="linha.key"
              :class="{ 'tr--destaque': linha.destaque }"
            >
              <td class="td-label">{{ linha.label }}</td>
              <td
                v-for="(valor, i) in linha.valores"
                :key="i"
                class="td-valor"
              >{{ fmt(valor) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Bloco 2: média 12 meses -->
      <div v-if="linhasMedia" class="bloco">
        <div class="bloco__head">
          <span class="bloco__titulo">2 — Estimativa de economia (com base no consumo médio)</span>
          <span class="bloco__badge">Bandeira Vigente</span>
        </div>
        <table class="tabela">
          <thead>
            <tr class="tr-info">
              <td colspan="2" class="td-info-label">
                <span>Consumo médio últimos 12 meses:</span>
                <span>Créditos de energia que você vai receber:</span>
              </td>
              <td colspan="3" class="td-info-valor">
                <span><strong>{{ fmtKwh(consumoMedioVal) }} KWH</strong></span>
                <span><strong>{{ fmtKwh(creditosMedia) }} KWH</strong></span>
              </td>
            </tr>
            <tr>
              <th class="th-consumo"></th>
              <th
                v-for="b in bandeiras"
                :key="b.key"
                class="th-bandeira"
                :class="'th--' + b.key"
              >{{ b.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="linha in linhasMedia"
              :key="linha.key"
              :class="{ 'tr--destaque': linha.destaque }"
            >
              <td class="td-label">{{ linha.label }}</td>
              <td
                v-for="(valor, i) in linha.valores"
                :key="i"
                class="td-valor"
              >{{ fmt(valor) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="disclaimer">
        Estimativa com base na tarifa da fatura enviada e no desconto exclusivo da AXEN.
        Os créditos de energia injetados na fatura de sua Concessionária levam em conta o seu consumo médio nos últimos 12 meses,
        e são atualizados a cada período de 3 meses considerando a nova média de consumo do período de 12 meses mais recente.
      </p>
    </template>

    <!-- Navegação -->
    <div class="actions-row">
      <button type="button" class="btn btn--ghost" @click="$emit('voltar')">
        ← Voltar
      </button>
      <button
        type="button"
        class="btn btn--primary"
        :disabled="!dadosValidos"
        @click="$emit('avancar')"
      >
        Quero economizar →
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "~@/design/tokens";

.simulacao {
  display: flex;
  flex-direction: column;
  gap: $space-5;

  @media (max-width: calc(#{$bp-md} - 1px)) {
    .card-fatura,
    .bloco {
      margin-left: calc(-1 * #{$space-5});
      margin-right: calc(-1 * #{$space-5});
      border-radius: 0;
    }
  }
}

// ── Card da fatura ───────────────────────────────────────────
.card-fatura {
  border-radius: $radius-input;
  border: 1px solid $border-subtle;
  overflow: hidden;
  box-shadow: $shadow-card;
}

.card-fatura__header {
  background: $brand-primary;
  color: $text-on-brand;
  padding: $space-3 $space-4;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-fatura__empresa {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .01em;
  text-transform: uppercase;
}

.card-fatura__ref {
  font-size: 12px;
  opacity: .75;
  font-weight: 500;
}

.card-fatura__body {
  padding: $space-4;
  border-bottom: 1px solid $border-subtle;
}

.card-fatura__nome {
  font-size: 16px;
  font-weight: 700;
  color: $text-strong;
  margin-bottom: $space-1;
}

.card-fatura__endereco,
.card-fatura__cidade {
  font-size: 13px;
  color: $text-muted;
  line-height: 1.5;
}

.card-fatura__footer {
  display: flex;
  gap: $space-5;
  padding: $space-3 $space-4;
  background: $bg-soft;
  flex-wrap: wrap;
}

.card-fatura__item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-fatura__item-label {
  font-size: 11px;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: .04em;
  font-weight: 600;
}

.card-fatura__item-valor {
  font-size: 15px;
  font-weight: 700;
  color: $text-strong;
}

// ── Bloco ────────────────────────────────────────────────────
.bloco {
  border-radius: $radius-input;
  overflow: hidden;
  border: 1px solid $border-subtle;
  box-shadow: $shadow-card;
}

.bloco__head {
  background: $brand-primary-darker;
  color: $text-on-brand;
  padding: $space-3 $space-4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $space-4;
}

.bloco__titulo {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .01em;
}

.bloco__badge {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  opacity: .8;
  text-transform: uppercase;
  letter-spacing: .05em;
}

// ── Tabela ───────────────────────────────────────────────────
.tabela {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead tr {
    background: $brand-primary;
  }

  th {
    padding: $space-2 $space-3;
    color: $text-on-brand;
    font-weight: 600;
    text-align: center;
    font-size: 12px;
    border-right: 1px solid rgba(255,255,255,.15);

    &:last-child { border-right: none; }
  }

  .th-consumo {
    text-align: left;
    width: 52%;
    font-weight: 400;
    font-size: 13px;
  }

  .tr-info {
    background: $brand-primary-soft;

    td {
      padding: $space-3 $space-4;
      font-size: 13px;
      border-bottom: 1px solid $border-subtle;
      vertical-align: middle;
    }

    .td-info-label {
      color: $text-muted;
      span { display: block; line-height: 1.9; }
    }

    .td-info-valor {
      color: $brand-primary-dark;
      text-align: right;
      span { display: block; line-height: 1.9; }
      strong { font-weight: 700; font-size: 14px; }
    }
  }

  // Cores das bandeiras no cabeçalho
  .th--verde       { background: #15803D; }
  .th--amarela     { background: #CA8A04; color: #1a1a1a; }
  .th--vermelha_p1 { background: #B91C1C; }
  .th--vermelha_p2 { background: #7F1D1D; }

  tbody tr {
    border-bottom: 1px solid $border-subtle;

    &:last-child { border-bottom: none; }
    &:hover { background: $bg-soft; }
  }

  td {
    padding: $space-2 $space-3;
    color: $text-body;
  }

  .td-label {
    font-size: 13px;
    color: $text-body;
  }

  .td-valor {
    text-align: center;
    font-weight: 600;
    color: $text-strong;
    white-space: nowrap;
  }

  // Linha de destaque (10 anos)
  .tr--destaque {
    background: $brand-primary-darker !important;

    td {
      color: $text-on-brand;
      font-weight: 700;
    }

    .td-valor {
      color: $brand-accent;
      font-size: 14px;
    }
  }
}

// ── Formulário manual ────────────────────────────────────────
.form-manual {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $space-3 $space-4;
  padding: $space-4;
  border: 1px solid $border-subtle;
  border-radius: $radius-input;
  background: $bg-soft;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: $text-body;
}

// ── Disclaimer ───────────────────────────────────────────────
.disclaimer {
  font-size: 12px;
  color: $text-muted;
  line-height: 1.5;
  margin: 0;
}

// ── Navegação ────────────────────────────────────────────────
.actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: $space-2;

  @media (max-width: calc(#{$bp-md} - 1px)) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: $space-3;
  }
}
</style>
